'use client'

import { useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react'
import type { LedgerStreamEntry } from '@/lib/ledger-mapping'
import { mapToLedgerEntry } from '@/lib/ledger-mapping'
import { STREAM_CATEGORIES } from '@/lib/stream'
import { getPublicLedgerStreams } from '@/app/actions/broadcast'
import LedgerEntryCard from '@/components/LedgerEntryCard'
import { SectionLabel } from './ui/SectionLabel'
import { Badge, PressableBadge } from './ui/Badge'
import { SearchIcon } from './icons/SearchIcon'
import { UserIcon } from './icons/UserIcon'

const LEDGER_PAGE_SIZE = 50

// ─── Types ──────────────────────────────────────────────────────────────────

type SortKey = 'date-desc' | 'date-asc' | 'title-asc' | 'title-desc'

interface Props {
  entries: LedgerStreamEntry[]
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function LedgerClient({ entries: initialEntries }: Props) {
  const [entries, setEntries] = useState(initialEntries)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(initialEntries.length >= LEDGER_PAGE_SIZE)
  const [search, setSearch] = useState('')
  const [usernameSearch, setUsernameSearch] = useState('')
  const [activeCategory, setActiveCategory] = useState<string>('all')
  const [sort, setSort] = useState<SortKey>('date-desc')
  const [isLoadingMore, startLoadMore] = useTransition()
  const [isSearching, startSearch] = useTransition()
  const sentinelRef = useRef<HTMLDivElement>(null)

  // ── Server-side search/filter (resets pagination) ─────────────────────────

  const serverSearch = useCallback(
    (searchVal: string, categoryVal: string) => {
      startSearch(async () => {
        const results = await getPublicLedgerStreams({
          page: 1,
          search: searchVal || undefined,
          category: categoryVal !== 'all' ? categoryVal : undefined,
        })
        setEntries(results.map(mapToLedgerEntry))
        setPage(1)
        setHasMore(results.length >= LEDGER_PAGE_SIZE)
      })
    },
    []
  )

  // Debounce search inputs
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>(undefined)
  function handleSearchChange(value: string) {
    setSearch(value)
    clearTimeout(searchTimeout.current)
    searchTimeout.current = setTimeout(() => {
      serverSearch(value, activeCategory)
    }, 400)
  }

  function handleUsernameChange(value: string) {
    setUsernameSearch(value)
    clearTimeout(searchTimeout.current)
    searchTimeout.current = setTimeout(() => {
      serverSearch(value, activeCategory)
    }, 400)
  }

  function handleCategoryChange(value: string) {
    setActiveCategory(value)
    serverSearch(search || usernameSearch, value)
  }

  function clearFilters() {
    setSearch('')
    setUsernameSearch('')
    setActiveCategory('all')
    serverSearch('', 'all')
  }

  // ── Infinite scroll ───────────────────────────────────────────────────────

  function loadMore() {
    if (isLoadingMore || !hasMore) return
    startLoadMore(async () => {
      const nextPage = page + 1
      const results = await getPublicLedgerStreams({
        page: nextPage,
        search: search || usernameSearch || undefined,
        category: activeCategory !== 'all' ? activeCategory : undefined,
      })
      setEntries((prev) => [...prev, ...results.map(mapToLedgerEntry)])
      setPage(nextPage)
      setHasMore(results.length >= LEDGER_PAGE_SIZE)
    })
  }

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !isLoadingMore) {
          loadMore()
        }
      },
      { rootMargin: '200px' }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, isLoadingMore, page, search, usernameSearch, activeCategory])

  // ── Client-side sort ──────────────────────────────────────────────────────

  const sorted = useMemo(() => {
    return [...entries].sort((a, b) => {
      switch (sort) {
        case 'date-desc':
          return b.streamedAt.localeCompare(a.streamedAt)
        case 'date-asc':
          return a.streamedAt.localeCompare(b.streamedAt)
        case 'title-asc':
          return a.title.localeCompare(b.title)
        case 'title-desc':
          return b.title.localeCompare(a.title)
      }
    })
  }, [entries, sort])

  // ── Client-side username filter ───────────────────────────────────────────

  const filtered = useMemo(() => {
    if (!usernameSearch) return sorted
    const q = usernameSearch.toLowerCase()
    return sorted.filter((e) => e.username?.toLowerCase().includes(q))
  }, [sorted, usernameSearch])

  const hasActiveFilters = search !== '' || usernameSearch !== '' || activeCategory !== 'all'

  if (initialEntries.length === 0 && !hasActiveFilters) {
    return <LedgerEmptyState />
  }

  return (
    <>
      {/* ── Header — matches Library page layout ─────────────────────────── */}
      <section
        data-part="ledger-header"
        className="section-top pt-20 sm:pt-28 pb-8 sm:pb-12 px-1 sm:px-6"
      >
        <div data-part="ledger-header-inner" className="max-w-6xl mx-auto pl-1 sm:pl-0">
          <SectionLabel className="mb-2 sm:mb-3">ON CHAIN</SectionLabel>
          <h1 className="font-bold mb-2 text-[clamp(24px,4vw,48px)]">
            The Ledger
          </h1>
          <p className="text-steel dark:text-cream/40 font-light text-[16px] mb-6 sm:mb-8">
            Publicly shared on-chain verifications &mdash; follow the TrueLORE.
          </p>

          {/* Search bar — matches Library search */}
          <div data-part="search-wrapper" className="relative max-w-lg mb-4 sm:mb-6">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-mist dark:text-cream/30" />
            <input
              data-part="search-input"
              type="text"
              placeholder="Search titles..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              aria-label="Search titles"
              className="w-full bg-white dark:bg-navy/40 border border-carolina/30 dark:border-carolina/20 rounded-lg pl-10 pr-4 py-2.5 sm:py-3
                font-grotesk text-[16px] text-ink dark:text-cream placeholder:text-mist dark:placeholder:text-cream/30
                focus:outline-none focus:border-carolina-dark dark:focus:border-carolina/60 focus:ring-2 focus:ring-carolina-dark/30 dark:focus:ring-carolina/30 focus:ring-offset-1 transition-colors"
            />
            {isSearching && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-carolina/30 border-t-carolina rounded-full animate-spin" />
            )}
          </div>

          {/* Username search */}
          <div data-part="username-search-wrapper" className="relative max-w-lg mb-4 sm:mb-6">
            <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-mist dark:text-cream/30" />
            <input
              data-part="username-search-input"
              type="text"
              placeholder="Search usernames..."
              value={usernameSearch}
              onChange={(e) => handleUsernameChange(e.target.value)}
              aria-label="Search usernames"
              className="w-full bg-white dark:bg-navy/40 border border-carolina/30 dark:border-carolina/20 rounded-lg pl-10 pr-4 py-2.5 sm:py-3
                font-grotesk text-[16px] text-ink dark:text-cream placeholder:text-mist dark:placeholder:text-cream/30
                focus:outline-none focus:border-carolina-dark dark:focus:border-carolina/60 focus:ring-2 focus:ring-carolina-dark/30 dark:focus:ring-carolina/30 focus:ring-offset-1 transition-colors"
            />
          </div>

          {/* Category pills — matches Library tag style */}
          <div data-part="category-filters" className="flex flex-wrap gap-2 mb-4">
            <PressableBadge active={activeCategory === 'all'} onClick={() => handleCategoryChange('all')}>ALL</PressableBadge>
            {STREAM_CATEGORIES.map((c) => (
              <PressableBadge
                key={c.value}
                active={activeCategory === c.value}
                onClick={() => handleCategoryChange(c.value)}
              >
                {c.label.toUpperCase()}
              </PressableBadge>
            ))}
          </div>

          {/* Sort + clear row */}
          <div className="flex items-center justify-between">
            <div className="flex flex-wrap gap-2">
              <PressableBadge active={sort === 'date-desc'} onClick={() => setSort('date-desc')}>DATE ↓</PressableBadge>
              <PressableBadge active={sort === 'date-asc'} onClick={() => setSort('date-asc')}>DATE ↑</PressableBadge>
              <PressableBadge active={sort === 'title-asc'} onClick={() => setSort('title-asc')}>A → Z</PressableBadge>
              <PressableBadge active={sort === 'title-desc'} onClick={() => setSort('title-desc')}>Z → A</PressableBadge>
            </div>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="font-mono text-[16px] tracking-[2px] text-carolina-dark dark:text-carolina hover:underline underline-offset-2"
              >
                CLEAR
              </button>
            )}
          </div>
        </div>
      </section>

      {/* ── Grid section — matches Library grid ──────────────────────────── */}
      <section data-part="ledger-grid-section" className="px-1 sm:px-6 py-8 sm:py-12">
        <div data-part="ledger-grid-inner" className="max-w-6xl mx-auto">
          {filtered.length === 0 ? (
            <div data-part="no-results" className="text-center py-24">
              <p className="font-mono text-[16px] tracking-[4px] text-mist dark:text-cream/20">
                NO RESULTS
              </p>
            </div>
          ) : (
            <>
              <p
                data-part="result-count"
                className="font-mono text-[16px] tracking-[2px] text-mist dark:text-cream/25 mb-4 sm:mb-6 pl-1 sm:pl-0"
              >
                {filtered.length} VERIFICATION{filtered.length !== 1 ? 'S' : ''}
                {hasMore ? '+' : ''}
              </p>
              <div
                data-part="ledger-grid"
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
              >
                {filtered.map((entry) => (
                  <LedgerEntryCard key={entry.id} entry={entry} />
                ))}
              </div>
            </>
          )}

          {/* Infinite scroll sentinel */}
          {hasMore && (
            <div ref={sentinelRef} className="flex justify-center py-8">
              {isLoadingMore && (
                <span className="w-5 h-5 border-2 border-carolina/30 border-t-carolina rounded-full animate-spin" />
              )}
            </div>
          )}

          <p className="font-mono text-[12px] tracking-[2px] text-mist dark:text-cream/20 text-center mt-12">
            TrueLORE TrAced Method&trade; &middot; Verifications are permanent on-chain records
          </p>
        </div>
      </section>
    </>
  )
}

// ── Empty State ──────────────────────────────────────────────────────────────

function LedgerEmptyState() {
  return (
    <section
      data-part="ledger-empty"
      className="relative flex flex-col items-center justify-center text-center px-1 sm:px-6 pt-32 sm:pt-40 pb-24 sm:pb-36 overflow-hidden"
    >
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none bg-hero-ambience" />
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none hero-grid" />

      <div className="relative z-10 max-w-2xl mx-auto">
        <Badge color="carolina" size="sm" className="mb-8">
          THE TRACED METHOD&trade;
        </Badge>

        <h1
          className="font-grotesk font-bold leading-none tracking-tight text-ink dark:text-cream mb-5 text-[clamp(32px,6vw,64px)]"
        >
          The{' '}
          <span className="text-grad-brand">TrueLORE</span>{' '}
          of the Ledger
        </h1>

        <p
          className="text-steel dark:text-cream/50 font-light leading-relaxed mx-auto text-[clamp(15px,2vw,18px)] max-w-[520px]"
        >
          Once verifications happen users can share TrueLORE links here. Links will
          take viewers directly to the on-chain transactions where they can verify the
          verifications. Truth is in the proof!
        </p>
      </div>
    </section>
  )
}
